import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Megaphone, Plus, Sparkles } from 'lucide-react';

import {
  acknowledgeAnnouncement,
  archiveAnnouncement,
  listAnnouncements,
  markAnnouncementRead,
  type Announcement,
} from '@/api/comms/announcements.api';
import { deleteShoutOut, listShoutOuts, type ShoutOut } from '@/api/comms/shoutOuts.api';
import { getVto, normalizeVtoData } from '@/api/eos/vto.api';
import { listDepartments } from '@/api/employees.api';
import { AnnouncementCard } from '@/components/comms/AnnouncementCard';
import { AnnouncementComposerModal } from '@/components/comms/AnnouncementComposerModal';
import { ReceiptsModal } from '@/components/comms/ReceiptsModal';
import { ShoutOutCard } from '@/components/comms/ShoutOutCard';
import { ShoutOutComposerModal } from '@/components/comms/ShoutOutComposerModal';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Tabs } from '@/components/ui/Tabs';
import { describeCommsError } from '@/lib/comms/errors';
import { hasCapability } from '@/lib/capabilities';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * Communication (Stream E / ADR-027 §7.2) — one page, two tabs:
 * Announcements (feed + composer + receipts) and Shout-outs (feed +
 * composer). Feeds poll every 60s and refetch on focus. Deep links:
 * `?tab=announcements|shoutouts` and `?compose=announcement|shoutout`.
 */

type CommunicationTab = 'announcements' | 'shoutouts';
const POLL_MS = 60_000;

export function CommunicationPage() {
  const { state } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const me = state.status === 'authenticated' ? state.me : null;

  const [activeTab, setActiveTab] = useState<CommunicationTab>(() =>
    searchParams.get('tab') === 'shoutouts' ? 'shoutouts' : 'announcements',
  );
  const [announcementComposerOpen, setAnnouncementComposerOpen] = useState(false);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [receiptsFor, setReceiptsFor] = useState<Announcement | null>(null);
  const [shoutOutComposerOpen, setShoutOutComposerOpen] = useState(false);
  /** From `?compose=shoutout&to=<membershipId>` — a streak nudge (ADR-048) lands here. */
  const [shoutOutInitialRecipient, setShoutOutInitialRecipient] = useState<string | null>(null);

  const canManageAnnouncements = me !== null && hasCapability(me, 'manage_announcements');
  const isDeptLead = me?.membership.role === 'department_lead';
  const canComposeAnnouncement = canManageAnnouncements || isDeptLead;
  const canModerateShoutOuts = me !== null && hasCapability(me, 'manage_eos');

  // ── Data ────────────────────────────────────────────────────────────
  const departmentsQuery = useQuery({ queryKey: ['departments'], queryFn: listDepartments });
  const departmentName = (id: string | null): string | undefined =>
    (departmentsQuery.data ?? []).find((d) => d.id === id)?.name;

  const vtoQuery = useQuery({
    queryKey: ['eos', 'vto'],
    queryFn: () => getVto(),
    staleTime: 5 * 60 * 1000,
  });
  const coreValues = useMemo(
    () => normalizeVtoData(vtoQuery.data?.current?.dataJson).coreValues.filter((v) => v.trim() !== ''),
    [vtoQuery.data],
  );

  const announcementsQuery = useInfiniteQuery({
    queryKey: ['comms', 'announcements'],
    queryFn: ({ pageParam }) => listAnnouncements(pageParam ? { before: pageParam } : {}),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasMore ? last.items.at(-1)?.publishedAt : undefined),
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });
  const announcements = useMemo(
    () => announcementsQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [announcementsQuery.data],
  );

  const shoutOutsQuery = useInfiniteQuery({
    queryKey: ['comms', 'shoutOuts'],
    queryFn: ({ pageParam }) => listShoutOuts(pageParam ? { before: pageParam } : {}),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasMore ? last.items.at(-1)?.createdAt : undefined),
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });
  const shoutOuts = useMemo(
    () => shoutOutsQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [shoutOutsQuery.data],
  );

  // ── Deep-link (?compose=…) ──────────────────────────────────────────
  useEffect(() => {
    const compose = searchParams.get('compose');
    if (!compose) return;
    if (compose === 'announcement' && canComposeAnnouncement) {
      setEditing(null);
      setAnnouncementComposerOpen(true);
      setActiveTab('announcements');
    } else if (compose === 'shoutout') {
      setShoutOutInitialRecipient(searchParams.get('to'));
      setShoutOutComposerOpen(true);
      setActiveTab('shoutouts');
    }
    const next = new URLSearchParams(searchParams);
    next.delete('compose');
    next.delete('to');
    setSearchParams(next, { replace: true });
  }, [searchParams, canComposeAnnouncement, setSearchParams]);

  function changeTab(id: CommunicationTab): void {
    setActiveTab(id);
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    setSearchParams(next, { replace: true });
  }

  function invalidateAnnouncements(): void {
    void queryClient.invalidateQueries({ queryKey: ['comms', 'announcements'] });
    void queryClient.invalidateQueries({ queryKey: ['comms', 'unread'] });
    void queryClient.invalidateQueries({ queryKey: ['eos', 'my-day'] });
  }
  function invalidateShoutOuts(): void {
    void queryClient.invalidateQueries({ queryKey: ['comms', 'shoutOuts'] });
    void queryClient.invalidateQueries({ queryKey: ['eos', 'my-day'] });
  }

  // ── Mutations ───────────────────────────────────────────────────────
  const markReadMutation = useMutation({
    mutationFn: (id: string) => markAnnouncementRead(id),
    onSuccess: invalidateAnnouncements,
    onError: (err) => {
      toast.show(describeCommsError(err, 'Could not mark that as read.'), { variant: 'danger' });
    },
  });
  const acknowledgeMutation = useMutation({
    mutationFn: (id: string) => acknowledgeAnnouncement(id),
    onSuccess: () => {
      invalidateAnnouncements();
      toast.show('Acknowledged — thanks.', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeCommsError(err, 'Could not acknowledge that.'), { variant: 'danger' });
    },
  });
  const archiveMutation = useMutation({
    mutationFn: (id: string) => archiveAnnouncement(id),
    onSuccess: () => {
      invalidateAnnouncements();
      toast.show('Announcement archived.', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeCommsError(err, 'Could not archive that.'), { variant: 'danger' });
    },
  });
  const deleteShoutOutMutation = useMutation({
    mutationFn: (id: string) => deleteShoutOut(id),
    onSuccess: () => {
      invalidateShoutOuts();
      toast.show('Shout-out removed.', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeCommsError(err, 'Could not remove that shout-out.'), { variant: 'danger' });
    },
  });

  if (!me) {
    return (
      <div className="flex justify-center py-16">
        <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
      </div>
    );
  }

  const canManageThisAnnouncement = (a: Announcement): boolean =>
    canManageAnnouncements || (isDeptLead && a.authorMembershipId === me.membership.id);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="typo-h1">Communication</h1>
          <p className="mt-1 text-[14px] text-kw-muted">
            Company announcements and peer recognition against our core values.
          </p>
        </div>
        {activeTab === 'announcements' ? (
          canComposeAnnouncement ? (
            <Button
              onClick={() => {
                setEditing(null);
                setAnnouncementComposerOpen(true);
              }}
            >
              <Plus className="size-4" />
              New announcement
            </Button>
          ) : null
        ) : (
          <Button
            onClick={() => {
              setShoutOutComposerOpen(true);
            }}
          >
            <Sparkles className="size-4" />
            Give a shout-out
          </Button>
        )}
      </div>

      <Tabs
        tabs={[
          { id: 'announcements', label: 'Announcements' },
          { id: 'shoutouts', label: 'Shout-outs' },
        ]}
        activeTab={activeTab}
        onChange={(id) => {
          changeTab(id as CommunicationTab);
        }}
      />

      {activeTab === 'announcements' ? (
        <AnnouncementsFeed
          isLoading={announcementsQuery.isLoading}
          error={announcementsQuery.error}
          announcements={announcements}
          departmentName={departmentName}
          canManageThis={canManageThisAnnouncement}
          actionPendingId={
            markReadMutation.isPending
              ? markReadMutation.variables
              : acknowledgeMutation.isPending
                ? acknowledgeMutation.variables
                : null
          }
          onMarkRead={(id) => {
            markReadMutation.mutate(id);
          }}
          onAcknowledge={(id) => {
            acknowledgeMutation.mutate(id);
          }}
          onViewReceipts={setReceiptsFor}
          onEdit={(a) => {
            setEditing(a);
            setAnnouncementComposerOpen(true);
          }}
          onArchive={(a) => {
            archiveMutation.mutate(a.id);
          }}
          hasNextPage={announcementsQuery.hasNextPage}
          isFetchingNextPage={announcementsQuery.isFetchingNextPage}
          onLoadMore={() => {
            void announcementsQuery.fetchNextPage();
          }}
        />
      ) : (
        <ShoutOutsFeed
          isLoading={shoutOutsQuery.isLoading}
          error={shoutOutsQuery.error}
          shoutOuts={shoutOuts}
          coreValuesEmpty={!vtoQuery.isLoading && coreValues.length === 0}
          currentMembershipId={me.membership.id}
          canModerate={canModerateShoutOuts}
          deletePendingId={deleteShoutOutMutation.isPending ? deleteShoutOutMutation.variables : null}
          onDelete={(id) => {
            deleteShoutOutMutation.mutate(id);
          }}
          hasNextPage={shoutOutsQuery.hasNextPage}
          isFetchingNextPage={shoutOutsQuery.isFetchingNextPage}
          onLoadMore={() => {
            void shoutOutsQuery.fetchNextPage();
          }}
        />
      )}

      <AnnouncementComposerModal
        open={announcementComposerOpen}
        onClose={() => {
          setAnnouncementComposerOpen(false);
          setEditing(null);
        }}
        me={me}
        editing={editing}
        onSaved={() => {
          invalidateAnnouncements();
          toast.show(editing ? 'Announcement updated.' : 'Announcement published.', {
            variant: 'success',
          });
        }}
      />

      <ReceiptsModal
        open={receiptsFor !== null}
        onClose={() => {
          setReceiptsFor(null);
        }}
        announcement={receiptsFor}
      />

      <ShoutOutComposerModal
        open={shoutOutComposerOpen}
        onClose={() => {
          setShoutOutComposerOpen(false);
          setShoutOutInitialRecipient(null);
        }}
        initialRecipientId={shoutOutInitialRecipient}
        coreValues={coreValues}
        currentMembershipId={me.membership.id}
        onCreated={() => {
          invalidateShoutOuts();
          toast.show('Shout-out posted.', { variant: 'success' });
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Announcements feed
// ─────────────────────────────────────────────────────────────────────────

function AnnouncementsFeed({
  isLoading,
  error,
  announcements,
  departmentName,
  canManageThis,
  actionPendingId,
  onMarkRead,
  onAcknowledge,
  onViewReceipts,
  onEdit,
  onArchive,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  isLoading: boolean;
  error: unknown;
  announcements: Announcement[];
  departmentName: (id: string | null) => string | undefined;
  canManageThis: (a: Announcement) => boolean;
  actionPendingId: string | null | undefined;
  onMarkRead: (id: string) => void;
  onAcknowledge: (id: string) => void;
  onViewReceipts: (a: Announcement) => void;
  onEdit: (a: Announcement) => void;
  onArchive: (a: Announcement) => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}) {
  if (isLoading) return <FeedSpinner />;
  if (error) return <FeedError kind="announcements" error={error} />;
  if (announcements.length === 0) {
    return (
      <Card className="flex flex-col items-center gap-2 p-12 text-center">
        <Megaphone className="size-10 text-kw-ghost" />
        <p className="text-[15px] font-medium text-kw-shark">No announcements yet</p>
        <p className="text-[13px] text-kw-muted">Company and department updates will show up here.</p>
      </Card>
    );
  }
  return (
    <div className="space-y-4">
      {announcements.map((a) => (
        <AnnouncementCard
          key={a.id}
          announcement={a}
          canManage={canManageThis(a)}
          departmentName={departmentName(a.departmentId)}
          actionPending={actionPendingId === a.id}
          onMarkRead={onMarkRead}
          onAcknowledge={onAcknowledge}
          onViewReceipts={onViewReceipts}
          onEdit={onEdit}
          onArchive={onArchive}
        />
      ))}
      {hasNextPage ? (
        <div className="flex justify-center">
          <Button variant="secondary" isLoading={isFetchingNextPage} onClick={onLoadMore}>
            Load more
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Shout-outs feed
// ─────────────────────────────────────────────────────────────────────────

function ShoutOutsFeed({
  isLoading,
  error,
  shoutOuts,
  coreValuesEmpty,
  currentMembershipId,
  canModerate,
  deletePendingId,
  onDelete,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  isLoading: boolean;
  error: unknown;
  shoutOuts: ShoutOut[];
  coreValuesEmpty: boolean;
  currentMembershipId: string;
  canModerate: boolean;
  deletePendingId: string | null | undefined;
  onDelete: (id: string) => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}) {
  if (isLoading) return <FeedSpinner />;
  if (error) return <FeedError kind="shout-outs" error={error} />;

  return (
    <div className="space-y-4">
      {coreValuesEmpty ? (
        <Card className="flex items-start gap-3 border-kw-warning-text/30 bg-kw-warning-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-warning-text" />
          <p className="text-[13px] text-kw-warning-text">
            No core values are defined yet — shout-outs are tagged to a core value, so define them on the{' '}
            <a href="/eos/vto" className="font-medium underline">
              V/TO
            </a>{' '}
            first.
          </p>
        </Card>
      ) : null}

      {shoutOuts.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 p-12 text-center">
          <Sparkles className="size-10 text-kw-ghost" />
          <p className="text-[15px] font-medium text-kw-shark">No shout-outs yet</p>
          <p className="text-[13px] text-kw-muted">
            Be the first to recognize a teammate for living a core value.
          </p>
        </Card>
      ) : (
        shoutOuts.map((s) => (
          <ShoutOutCard
            key={s.id}
            shoutOut={s}
            canDelete={s.giverMembershipId === currentMembershipId || canModerate}
            deletePending={deletePendingId === s.id}
            onDelete={onDelete}
          />
        ))
      )}

      {hasNextPage ? (
        <div className="flex justify-center">
          <Button variant="secondary" isLoading={isFetchingNextPage} onClick={onLoadMore}>
            Load more
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Shared feed chrome
// ─────────────────────────────────────────────────────────────────────────

function FeedSpinner() {
  return (
    <div className="flex justify-center py-16">
      <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
    </div>
  );
}

function FeedError({ kind, error }: { kind: string; error: unknown }) {
  return (
    <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
      <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
      <div>
        <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load {kind}</p>
        <p className="mt-1 text-[13px] text-kw-danger-text/80">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    </Card>
  );
}
